import { type ReactNode } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { CarIcon, UserIcon } from '@phosphor-icons/react';
import { Image } from '@/components';
import { cn } from '@/lib/utils';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { getEventEntitiesCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import type { EventEntities } from '@/types/event';
import {
    getEntityKindForClass,
    type EntityKind,
} from '@/utils/detection-classes';
import { UpdatePersonModal } from '@/features/PersonGrid/components/UpdatePersonModal';
import { AddVehicleModal } from '@/features/VehicleGrid/components/AddVehicleModal';
import { useEventEntityLink } from '../hooks/useEventEntityLink';
import { useEventEntityEditors } from '../hooks/useEventEntityEditors';
import { EventEntityMenu } from './EventEntityMenu';
import { LinkEntityPicker } from './LinkEntityPicker';

interface EventLinkedEntityPanelProps {
    eventId: string;
    classId?: number;
    returnUrl?: string;
}

export function EventLinkedEntityPanel({
    eventId,
    classId,
    returnUrl,
}: EventLinkedEntityPanelProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { t: tDirectory } = useTranslation(I18nNamespace.DIRECTORY);

    const { data: entitiesData, mutate } = useSWR<EventEntities>(
        eventId ? getEventEntitiesCacheKey(eventId) : null,
        () => EngineService.getEventEntities(eventId),
    );

    const entityKind = getEntityKindForClass(classId);
    const vehicleEntry = entitiesData?.vehicles?.[0];
    const personEntry = entitiesData?.persons?.[0];

    const resolvedKind: EntityKind | null =
        entityKind === 'vehicle' && vehicleEntry
            ? 'vehicle'
            : entityKind === 'person' && personEntry
              ? 'person'
              : vehicleEntry
                ? 'vehicle'
                : personEntry
                  ? 'person'
                  : null;

    const { unlink, link } = useEventEntityLink({
        eventId,
        entities: entitiesData,
        entityKind: resolvedKind,
    });

    const {
        editPerson,
        editVehicle,
        editPersonId,
        editVehicleId,
        setEditPersonId,
        setEditVehicleId,
        handleUpdatePerson,
        handleUpdateVehicle,
        duplicatePlateDialog,
    } = useEventEntityEditors(mutate);

    if (!resolvedKind) return null;

    const details =
        resolvedKind === 'vehicle' && vehicleEntry
            ? {
                  href: generatePath(AppRoutes.VEHICLE, {
                      vehicleId: vehicleEntry.vehicle.id,
                  }),
                  name:
                      vehicleEntry.vehicle.name ||
                      tDirectory('vehicles.card.unknownVehicle'),
                  imageUrl: vehicleEntry.vehicle.thumbnailUrl,
                  fallbackIcon: <CarIcon className="text-grey-300" size={28} />,
                  plate: vehicleEntry.licensePlateText,
                  menu: (
                      <EventEntityMenu
                          isUnknown={!vehicleEntry.vehicle.known}
                          replaceContent={(onClose) => (
                              <LinkEntityPicker
                                  entityKind="vehicle"
                                  sourceEntityId={vehicleEntry.vehicle.id}
                                  onLink={(id) => void link(id)}
                                  onClose={onClose}
                                  title={t(
                                      'eventView.entityMenu.replaceLinkedEntity',
                                  )}
                              />
                          )}
                          onUnlink={() => void unlink()}
                          onEdit={() =>
                              setEditVehicleId(vehicleEntry.vehicle.id)
                          }
                      />
                  ),
              }
            : resolvedKind === 'person' && personEntry
              ? {
                    href: generatePath(AppRoutes.PERSON, {
                        personId: personEntry.person.id,
                    }),
                    name:
                        personEntry.person.name ||
                        t('eventView.linkEntity.unknownPerson'),
                    imageUrl: personEntry.person.imageUrl,
                    fallbackIcon: (
                        <UserIcon className="text-grey-300" size={28} />
                    ),
                    plate: null as string | null,
                    menu: (
                        <EventEntityMenu
                            isUnknown={!personEntry.person.name}
                            replaceContent={(onClose) => (
                                <LinkEntityPicker
                                    entityKind="person"
                                    sourceEntityId={personEntry.person.id}
                                    onLink={(id) => void link(id)}
                                    onClose={onClose}
                                    title={t(
                                        'eventView.entityMenu.replaceLinkedEntity',
                                    )}
                                />
                            )}
                            onUnlink={() => void unlink()}
                            onEdit={() =>
                                setEditPersonId(personEntry.person.id)
                            }
                        />
                    ),
                }
              : null;

    if (!details) return null;

    return (
        <div className="flex w-full flex-col gap-6">
            <span className="text-sm-medium text-grey-200">
                {t('eventView.linkedEntity.title')}
            </span>
            <div className="group flex items-center gap-3">
                <EntityThumbnail
                    href={details.href}
                    imageUrl={details.imageUrl}
                    name={details.name}
                    fallbackIcon={details.fallbackIcon}
                    returnUrl={returnUrl}
                    className="h-14 w-14 rounded"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <Link
                        to={details.href}
                        state={returnUrl ? { returnUrl } : undefined}
                        className="text-sm-medium text-white-100 truncate no-underline"
                    >
                        {details.name}
                    </Link>
                    {details.plate && (
                        <p className="truncate text-xs text-grey-300">
                            {details.plate}
                        </p>
                    )}
                </div>
                <div className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 has-[[data-state=open]]:opacity-100">
                    {details.menu}
                </div>
            </div>
            {editPerson && (
                <UpdatePersonModal
                    isOpen={Boolean(editPersonId)}
                    onClose={() => setEditPersonId(null)}
                    mode="edit"
                    person={editPerson}
                    onUpdate={handleUpdatePerson}
                />
            )}
            {editVehicle && (
                <AddVehicleModal
                    isOpen={Boolean(editVehicleId)}
                    onClose={() => setEditVehicleId(null)}
                    mode="edit"
                    vehicle={editVehicle}
                    onUpdate={handleUpdateVehicle}
                />
            )}
            {duplicatePlateDialog}
        </div>
    );
}

interface EntityThumbnailProps {
    href: string;
    imageUrl: string | null | undefined;
    name: string;
    fallbackIcon: ReactNode;
    returnUrl?: string;
    className?: string;
}

function EntityThumbnail({
    href,
    imageUrl,
    name,
    fallbackIcon,
    returnUrl,
    className,
}: EntityThumbnailProps) {
    return (
        <Link
            to={href}
            state={returnUrl ? { returnUrl } : undefined}
            aria-label={name}
            className={cn(
                'flex shrink-0 items-center justify-center overflow-hidden bg-grey-600 outline-none',
                className,
            )}
        >
            {imageUrl ? (
                <Image
                    src={imageUrl}
                    alt={name}
                    auth
                    className="h-full w-full object-cover"
                />
            ) : (
                fallbackIcon
            )}
        </Link>
    );
}
